import { z } from 'zod';
import { PROCESSION_DAY_LABELS } from './season.ts';

/**
 * Arguments of the chatbot tools. They validate REST requests and LLM tool calls,
 * and their JSON Schema (z.toJSONSchema) is what the LLM sees, so the descriptions
 * are part of the prompt.
 */

/** Nature of a value: Consejo schedule, our own calculation, or live GPS. */
export type DataKind = 'official' | 'estimated' | 'live';

const brotherhood = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .describe(
    'Brotherhood name: the one returned by find_brotherhood, or as the user wrote it. ' +
      'If it is ambiguous, the tool says so and lists the candidates.',
  );

const year = z
  .number()
  .int()
  .min(2000)
  .max(2100)
  .optional()
  .describe(
    'Schedule year. Omit it to use the current or upcoming Semana Santa. Only pass an ' +
      'earlier year when the user accepts that schedule as a reference.',
  );

const place = z
  .string()
  .trim()
  .min(2)
  .max(100)
  .describe('Street, square or landmark as the user wrote it ("Sierpes", "la Campana")');

const localDateTime = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'Expected YYYY-MM-DDTHH:mm')
  .describe('Seville local date and time, YYYY-MM-DDTHH:mm');

const day = z.enum(PROCESSION_DAY_LABELS).describe('Procession day');

export const toolArgs = {
  find_brotherhood: z.object({
    text: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .describe('Name as the user wrote it ("la Borriquita", "el Cachorro")'),
  }),
  brotherhood_info: z.object({ brotherhood }),
  brotherhood_schedule: z.object({ brotherhood, year }),
  brotherhoods_of_day: z.object({ day, year }),
  itinerary: z.object({ brotherhood, year }),
  passing_time: z.object({ brotherhood, place, year }),
  what_passes_by: z.object({
    place,
    from: localDateTime,
    to: localDateTime.optional().describe('End of the time range; defaults to from + 2 h'),
  }),
  day_status: z.object({
    day: day.optional().describe('Procession day; omit it for the days in progress right now'),
  }),
} as const;

export type ToolName = keyof typeof toolArgs;
export type ToolArgs<N extends ToolName> = z.infer<(typeof toolArgs)[N]>;

export const toolDescriptions: Record<ToolName, string> = {
  find_brotherhood:
    'Resolves a popular name to brotherhood candidates. Call it first whenever the user ' +
    'names a brotherhood. If the result is ambiguous, ask the user which one they mean.',
  brotherhood_info:
    'Static facts about a brotherhood (church, number of floats, titular images, ' +
    'approximate nazarenes, website), each with its source and year. Missing fields are null.',
  brotherhood_schedule:
    'Official schedule of ONE brotherhood: its procession day and date and, at departure, ' +
    'Carrera Oficial (Venia en Campana), Cathedral and entry, when the guide cross ' +
    '(guide_cross) and the last float (last_float) pass. Use it for "¿a qué hora sale / ' +
    'entra en Carrera Oficial / se recoge X?".',
  brotherhoods_of_day:
    'All brotherhoods of a procession day, with their official departure, Carrera Oficial ' +
    '(Campana) and entry times. For a single brotherhood use brotherhood_schedule.',
  itinerary:
    'Ordered route of a brotherhood: official route text, official checkpoints and the ' +
    'streets with estimated times. For when it passes ONE place, use passing_time instead.',
  passing_time:
    'When the guide cross and the last float pass a place on the route of a brotherhood.',
  what_passes_by: 'Brotherhoods that pass a place within a time range.',
  day_status:
    'For a procession day, which brotherhoods have not departed yet, are on the street or ' +
    'are back, according to the schedule and the current time.',
};
